import { useCallback, useEffect, useState } from 'react';

import * as DataListAPI from '../../../../api/dataList';
import * as FiscalPerfilAPI from '../../../../api/fiscalPerfil';
import * as VerificadoresAPI from '../../../../api/verificadores';
import useFlashMessage from '../../../../hooks/useFlashMessage';

const CAMPOS_DECIMAIS = [
    'aliquotaIcms',
    'reducaoBaseIcms',
    'mvaIcmsSt',
    'aliquotaIcmsSt',
    'reducaoBaseIcmsSt',
    'aliquotaFcp',
    'aliquotaFcpSt',
    'aliquotaFcpStRetido',
    'aliquotaIpi',
    'aliquotaPis',
    'valorPautaPis',
    'aliquotaCofins',
    'valorPautaCofins',
];

function dataAtual() {
    return new Date().toISOString().slice(0, 10);
}

function criarRegraInicial(perfil) {
    return {
        ativo: true,
        operacaoId: '',
        ufDestinoId: '',
        tipoDestinatario: 'TODOS',
        consumidorFinal: '',
        vigenciaInicio: dataAtual(),
        vigenciaFim: '',

        cstIcms: '',
        csosn: '',
        aliquotaIcms: '',
        reducaoBaseIcms: '',

        mvaIcmsSt: '',
        aliquotaIcmsSt: '',
        reducaoBaseIcmsSt: '',

        aliquotaFcp: '',
        aliquotaFcpSt: '',
        aliquotaFcpStRetido: '',

        cstIpi: '',
        aliquotaIpi: '',

        cstPis: '',
        aliquotaPis: '',
        valorPautaPis: '',

        cstCofins: '',
        aliquotaCofins: '',
        valorPautaCofins: '',

        unidadePauta: '',

        cstIbsCbs: '',
        cClassTrib: '',

        origemCadastro: 'MANUAL',
        revisaoPendente: false,
        integracaoAtivo: false,
        integracaoTodasEmpresas: Boolean(perfil?.integracaoTodasEmpresas),
        observacao: '',
    };
}

function textoOuNulo(value) {
    const texto = String(value ?? '').trim();

    return texto || null;
}

function inteiroOuNulo(value) {
    if (value === undefined || value === null || value === '') {
        return null;
    }

    const numero = Number(value);

    return Number.isInteger(numero) && numero > 0 ? numero : null;
}

function decimalOuNulo(value) {
    if (value === undefined || value === null || value === '') {
        return null;
    }

    const numero = Number(String(value).replace(',', '.'));

    return Number.isFinite(numero) ? numero : null;
}

function consumidorFinalOuNulo(value) {
    if (value === true || value === 'true') {
        return true;
    }

    if (value === false || value === 'false') {
        return false;
    }

    return null;
}

function normalizarRegraParaFormulario(regra, perfil) {
    const inicial = criarRegraInicial(perfil);

    const normalizada = {
        ...inicial,
        ...regra,

        operacaoId: regra?.operacaoId ?? '',
        ufDestinoId: regra?.ufDestinoId ?? '',

        consumidorFinal:
            regra?.consumidorFinal === null || regra?.consumidorFinal === undefined ? '' : String(Boolean(regra.consumidorFinal)),

        vigenciaInicio: regra?.vigenciaInicio || inicial.vigenciaInicio,

        vigenciaFim: regra?.vigenciaFim || '',

        observacao: regra?.observacao || '',
    };

    for (const campo of CAMPOS_DECIMAIS) {
        normalizada[campo] = regra?.[campo] ?? '';
    }

    return normalizada;
}

function montarPayload(dados) {
    const payload = {
        ativo: Boolean(dados.ativo),

        operacaoId: inteiroOuNulo(dados.operacaoId),

        ufDestinoId: inteiroOuNulo(dados.ufDestinoId),

        tipoDestinatario: dados.tipoDestinatario || 'TODOS',

        consumidorFinal: consumidorFinalOuNulo(dados.consumidorFinal),

        vigenciaInicio: dados.vigenciaInicio,
        vigenciaFim: textoOuNulo(dados.vigenciaFim),

        cstIcms: textoOuNulo(dados.cstIcms),
        csosn: textoOuNulo(dados.csosn),

        reducaoBaseIcms: decimalOuNulo(dados.reducaoBaseIcms),

        mvaIcmsSt: decimalOuNulo(dados.mvaIcmsSt),

        aliquotaIcmsSt: decimalOuNulo(dados.aliquotaIcmsSt),

        reducaoBaseIcmsSt: decimalOuNulo(dados.reducaoBaseIcmsSt),

        aliquotaFcp: decimalOuNulo(dados.aliquotaFcp),

        aliquotaFcpSt: decimalOuNulo(dados.aliquotaFcpSt),

        aliquotaFcpStRetido: decimalOuNulo(dados.aliquotaFcpStRetido),

        cstIpi: textoOuNulo(dados.cstIpi),

        aliquotaIpi: decimalOuNulo(dados.aliquotaIpi),

        cstPis: textoOuNulo(dados.cstPis),

        aliquotaPis: decimalOuNulo(dados.aliquotaPis),

        valorPautaPis: decimalOuNulo(dados.valorPautaPis),

        cstCofins: textoOuNulo(dados.cstCofins),

        aliquotaCofins: decimalOuNulo(dados.aliquotaCofins),

        valorPautaCofins: decimalOuNulo(dados.valorPautaCofins),

        unidadePauta: textoOuNulo(dados.unidadePauta),

        cstIbsCbs: textoOuNulo(dados.cstIbsCbs),

        cClassTrib: textoOuNulo(dados.cClassTrib),

        origemCadastro: dados.origemCadastro || 'MANUAL',

        revisaoPendente: Boolean(dados.revisaoPendente),

        integracaoAtivo: Boolean(dados.integracaoAtivo),

        integracaoTodasEmpresas: Boolean(dados.integracaoTodasEmpresas),

        observacao: textoOuNulo(dados.observacao),
    };

    payload.aliquotaIcms = decimalOuNulo(dados.aliquotaIcms);

    return payload;
}

export default function useFiscalRegraModule({ perfil, recarregarPerfil }) {
    const { setFlashMessage } = useFlashMessage();

    const [dialogAberto, setDialogAberto] = useState(false);

    const [regraEditando, setRegraEditando] = useState(null);

    const [dadosRegra, setDadosRegra] = useState(() => criarRegraInicial(perfil));

    const [optionsOperacao, setOptionsOperacao] = useState([]);

    const [optionsUf, setOptionsUf] = useState([]);

    const [carregandoOpcoes, setCarregandoOpcoes] = useState(false);

    const [salvando, setSalvando] = useState(false);

    const [regraExcluir, setRegraExcluir] = useState(null);

    const carregarOpcoes = useCallback(async () => {
        if (!perfil?.empresaId) return;

        setCarregandoOpcoes(true);

        try {
            const [operacoes] = await Promise.all([
                DataListAPI.dataList({
                    empresaId: 1,
                    tabela: 'Operacao',
                    id: 'id',
                    descricao: 'descricao',
                    filtro: '',
                    contabil: true, //exige que a operação seja fiscal/contabil
                    order: 'descricao',
                }),
            ]);

            setOptionsOperacao(Array.isArray(operacoes) ? operacoes : []);
        } finally {
            setCarregandoOpcoes(false);
        }
    }, [perfil?.empresaId]);

    //carregar API's Externas
    const consultaUF = async () => {
        let data = await VerificadoresAPI.uf('');
        setOptionsUf(data);
    };

    useEffect(() => {
        carregarOpcoes();
        consultaUF();
    }, [carregarOpcoes]);

    function abrirNovaRegra() {
        setRegraEditando(null);

        setDadosRegra(criarRegraInicial(perfil));

        setDialogAberto(true);
    }

    function abrirEdicaoRegra(regra) {
        setRegraEditando(regra);

        setDadosRegra(normalizarRegraParaFormulario(regra, perfil));

        setDialogAberto(true);
    }

    function fecharDialog() {
        if (salvando) return;

        setDialogAberto(false);
        setRegraEditando(null);
    }

    function handleChange(event) {
        const { name, value, type, checked } = event.target;

        setDadosRegra((anterior) => {
            const proximo = {
                ...anterior,
                [name]: type === 'checkbox' ? checked : value,
            };

            if (name === 'ativo' && !checked) {
                proximo.integracaoAtivo = false;
                proximo.integracaoTodasEmpresas = false;
            }

            if (name === 'revisaoPendente' && checked) {
                proximo.integracaoAtivo = false;
                proximo.integracaoTodasEmpresas = false;
            }

            if (name === 'integracaoAtivo' && checked) {
                proximo.ativo = true;
                proximo.revisaoPendente = false;
            }

            if (name === 'integracaoAtivo' && !checked) {
                proximo.integracaoTodasEmpresas = false;
            }

            if (name === 'integracaoTodasEmpresas' && checked) {
                proximo.ativo = true;
                proximo.revisaoPendente = false;
                proximo.integracaoAtivo = true;
            }

            return proximo;
        });
    }

    async function salvarRegra() {
        if (!perfil?.uuid) return;

        setSalvando(true);

        try {
            const payload = montarPayload(dadosRegra);

            const response = regraEditando?.uuid
                ? await FiscalPerfilAPI.editarRegra(perfil.uuid, regraEditando.uuid, payload)
                : await FiscalPerfilAPI.cadastrarRegra(perfil.uuid, payload);

            setFlashMessage(response.data.message, response.data.type);

            setDialogAberto(false);
            setRegraEditando(null);

            await recarregarPerfil?.();
        } catch (error) {
            setFlashMessage(
                error.response?.data?.message || 'Não foi possível salvar a regra fiscal.',
                error.response?.data?.type || 'error'
            );
        } finally {
            setSalvando(false);
        }
    }

    function solicitarExclusao(regra) {
        setRegraExcluir(regra);
    }

    function cancelarExclusao() {
        setRegraExcluir(null);
    }

    async function confirmarExclusao() {
        if (!perfil?.uuid || !regraExcluir?.uuid) {
            return;
        }

        try {
            const response = await FiscalPerfilAPI.excluirRegra(perfil.uuid, regraExcluir.uuid);

            setRegraExcluir(null);

            setFlashMessage(response.data.message, response.data.type);

            await recarregarPerfil?.();
        } catch (error) {
            setFlashMessage(
                error.response?.data?.message || 'Não foi possível excluir a regra fiscal.',
                error.response?.data?.type || 'error'
            );
        }
    }

    return {
        state: {
            dialogAberto,
            regraEditando,
            dadosRegra,
            optionsOperacao,
            optionsUf,
            carregandoOpcoes,
            salvando,
            regraExcluir,
        },

        actions: {
            abrirNovaRegra,
            abrirEdicaoRegra,
            fecharDialog,
            handleChange,
            salvarRegra,
            solicitarExclusao,
            cancelarExclusao,
            confirmarExclusao,
            carregarOpcoes,
        },
    };
}
